import { Modal } from 'antd';
import { DocumentText, Gallery } from 'iconsax-react';
import React, { useState } from 'react';
import Helpers from '../../lib/helpers';
import classNames from 'classnames';

type AttachmentItem = {
  name: string;
  profileId: string;
  blobSize: number;
  blobFileUrl?: string;
};

type BusinessCardAttachmentProps = {
  title: string;
  attachments?: AttachmentItem[];
  withoutMargin?: boolean;
};

const formatFileSize = (bytes: number): string => {
  if (!bytes) return '0 MB';
  const mb = bytes / 1024 / 1024;
  return mb >= 1 ? `${Math.round(mb)} MB` : `${(mb * 1024).toFixed(0)} KB`;
};

const isImageFile = (name: string) =>
  /\.(jpe?g|png|gif|webp|bmp|svg)(\?|$)/i.test(name ?? '');

const isPdfFile = (name: string) => /\.pdf(\?|$)/i.test(name ?? '');

export const FileItem = ({
  name,
  size,
  url,
  onPreview,
}: {
  name: string;
  size: number;
  url?: string;
  onPreview: (url: string, name: string) => void;
}) => {
  const handleClick = () => {
    if (!url) return;
    onPreview(url, name);
  };

  return (
    <div
      className="flex items-center justify-between py-2 cursor-pointer hover:bg-gray-50 rounded-lg px-1 transition-colors"
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <div className="flex items-center gap-3">
        {isPdfFile(name) ? (
          <DocumentText variant="Bold" size={20} />
        ) : (
          <Gallery variant="Bold" size={20} />
        )}
        <span className="text-sm text-[#090909] font-normal" title={name}>
          {Helpers.truncateText(name || '', 35)}
        </span>
      </div>

      {!!size && (
        <span className="text-sm text-[#637381] font-normal">
          {formatFileSize(size)}
        </span>
      )}
    </div>
  );
};

export const BusinessCardAttachment = ({
  title,
  attachments,
  withoutMargin,
}: BusinessCardAttachmentProps) => {
  const [preview, setPreview] = useState<{ url: string; name: string } | null>(
    null
  );

  if (!attachments || attachments.length === 0) return null;

  const isImage = preview ? isImageFile(preview.name) : false;

  return (
    <>
      <div
        className={classNames(
          'body-card-attachments flex flex-col gap-2',
          !withoutMargin && 'mb-4'
        )}
      >
        {!!title && (
          <h2 className="text-base font-bold leading-6 text-[#090909]">
            {title}
          </h2>
        )}
        <div className="flex flex-col">
          {attachments?.map((item, index) => (
            <FileItem
              key={item?.profileId || index}
              name={item?.name || 'Attachment'}
              size={item?.blobSize || 0}
              url={item?.blobFileUrl}
              onPreview={async (url, name) => {
                if (isPdfFile(name)) {
                  const previewUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(
                    url
                  )}&embedded=true`;
                  window.open(previewUrl, '_blank');
                } else {
                  setPreview({ url, name });
                }
              }}
            />
          ))}
        </div>
      </div>

      <Modal
        open={!!preview}
        onCancel={() => setPreview(null)}
        footer={null}
        width="90vw"
        style={{ maxWidth: 900 }}
        title={preview?.name}
        destroyOnHidden
        centered
      >
        {preview && (
          <div className="flex justify-center bg-gray-50 rounded-lg min-h-[60vh] overflow-auto">
            {isImage ? (
              <img
                src={preview.url}
                alt={preview.name}
                className="max-w-full max-h-[70vh] object-contain"
              />
            ) : (
              <iframe
                title={preview.name}
                src={preview.url}
                className="w-full min-h-[60vh] border-0 rounded-lg"
              />
            )}
          </div>
        )}
      </Modal>
    </>
  );
};

export default BusinessCardAttachment;
